import React, { useEffect, useRef, useState } from "react";
import { KeenIcon, Scrollspy } from "@/components";
import { Modal, ModalContent, ModalBody, ModalHeader } from "@/components/modal";
import { useResponsive, useViewport } from "@/hooks";

import { CompanyDetailsData } from "@/pages/buddy/types/CompanyDetailsData";
import { JobDetailsData } from "@/pages/buddy/types/JobDetailsData";
import {SidebarModalMenu} from "@/pages/buddy/shared/new-soruce/SidebarModelMenu.tsx";
import {CompanySelection} from "@/pages/buddy/job/blocks/source/CompanySelection.tsx";
import {Intro} from "@/pages/buddy/job/blocks/source/Intro.tsx";
import {UploadDocument} from "@/pages/buddy/shared/new-soruce/UploadDocument.tsx";
import {UploadText} from "@/pages/buddy/shared/new-soruce/UploadText.tsx";
import {CrawlWebpage} from "@/pages/buddy/shared/new-soruce/CrawlWebpage.tsx";
import {CrawlLinkedIn} from "@/pages/buddy/shared/new-soruce/CrawlLinkedIn.tsx";

interface JobModalProps {
    open: boolean;
    onClose: () => void;
    mode: "create" | "addSource";
    preselectedCompany?: CompanyDetailsData | null;
    job?: JobDetailsData | null;
    refreshJobData?: () => void;
}

const JobModal: React.FC<JobModalProps> = ({
                                               open,
                                               onClose,
                                               mode,
                                               preselectedCompany = null,
                                               job = null,
                                               refreshJobData,
                                           }) => {
    const isCreateMode = mode === "create";
    const isAddSourceMode = mode === "addSource";

    // In creation mode, require a company selection
    const [selectedCompany, setSelectedCompany] = useState<CompanyDetailsData | null>(
        isCreateMode ? preselectedCompany : null
    );

    const desktopMode = useResponsive("up", "lg");
    const navBar = useRef<any | null>(null);
    const parentRef = useRef<any | null>(null);
    const [sidebarHeight, setSidebarHeight] = useState<number>(0);
    const [viewportHeight] = useViewport();
    const offset = 260;

    useEffect(() => {
        setSidebarHeight(viewportHeight - offset);
    }, [viewportHeight]);

    const getTitle = (): string => {
        if (isCreateMode) {
            return selectedCompany
                ? `Add New Job Profile for ${selectedCompany.name}`
                : "Add New Job Profile";
        }
        if (isAddSourceMode && job) {
            return `Add New Source to Job ${job.name}`;
        }
        return "Job";
    };

    // The parent entity is what will be passed along to the child components.
    const entity =
        isCreateMode
            ? selectedCompany
            : job
                ? {
                    p_id: job.p_id,
                    name: job.name,
                }
                : null;

    return (
        <Modal open={open} onClose={onClose} className="!flex">
            <ModalContent className="container-fixed px-30 overflow-hidden pt-7.5 my-[3%] w-full">
                <ModalHeader className="p-0 border-0">
                    <div className="flex items-center justify-between flex-wrap grow gap-5 pb-7.5">
                        <div className="flex items-baseline space-x-2">
                            <KeenIcon icon="teacher" className="text-3xl" />
                            <h1 className="text-xl font-semibold leading-none text-gray-900">{getTitle()}</h1>
                        </div>
                        <div className="flex items-center gap-3">
                            {/* In creation mode, allow changing the selected company if one is chosen */}
                            {isCreateMode && selectedCompany && (
                                <button
                                    className="btn btn-sm btn-light text-gray-600 hover:text-primary"
                                    onClick={() => setSelectedCompany(null)}
                                >
                                    Change Company
                                </button>
                            )}
                            <button className="btn btn-sm btn-light" onClick={onClose}>
                                Close
                            </button>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="scrollable-y py-0 mb-5 ps-0 pe-3 -me-5.5" ref={parentRef}>
                    <div className="flex grow gap-5 lg:gap-7.5">
                        {/* Show sidebar only if in desktop mode and a company is selected */}
                        {desktopMode && (
                            <div className="block w-[230px] shrink-0">
                                <div
                                    ref={navBar}
                                    className="w-[230px] fixed z-10 scrollable-y-auto"
                                    style={{ maxHeight: `${sidebarHeight}px` }}
                                >
                                    <Scrollspy offset={100} targetRef={parentRef}>
                                        <SidebarModalMenu />
                                    </Scrollspy>
                                </div>
                            </div>
                        )}
                        <div className="flex flex-col items-stretch grow gap-5 lg:gap-7.5">
                            {/* If in creation mode and no company is selected, show CompanySelection */}
                            {isCreateMode && !selectedCompany ? (
                                <CompanySelection onSelectCompany={setSelectedCompany} />
                            ) : (
                                <>
                                    <Intro mode={mode} />
                                    <UploadDocument
                                        parentEntity={entity}
                                        objectEntity={entity}
                                        entityType="job"
                                        mode={mode}
                                        onSuccess={refreshJobData}
                                    />
                                    <UploadText
                                        parentEntity={entity}
                                        objectEntity={entity}
                                        entityType="job"
                                        mode={mode}
                                        onSuccess={refreshJobData}
                                    />
                                    <CrawlWebpage
                                        parentEntity={entity}
                                        objectEntity={entity}
                                        entityType="job"
                                        mode={mode}
                                        onSuccess={refreshJobData}
                                    />
                                    <CrawlLinkedIn />
                                </>
                            )}
                        </div>
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export { JobModal };
